import { graphql, useMutation } from "react-relay"
import {
  ConsultQueueInput,
  useConsultQueueMutation as useConsultQueueMutationType,
} from "@/modules/telephony/services/__generated__/useConsultQueueMutation.graphql"
import { useGenesysMutationErrorHandling } from "@/modules/telephony/services/useGenesysMutationErrorHandling"

const consultQueueMutation = graphql`
  mutation useConsultQueueMutation($input: ConsultQueueInput!) {
    consultQueue(input: $input) {
      errors {
        ... on AgentNotAuthenticated {
          __typename
        }
        ... on InvalidPatientPhoneNumber {
          __typename
        }
        ... on NoActiveCallFound {
          __typename
        }
        ... on SeveralActiveCallsFound {
          __typename
        }
        ... on PatientNotOnCall {
          __typename
        }
        ... on ConsultAlreadyInProgress {
          __typename
        }
        ... on ConsultRejected {
          __typename
        }
      }
    }
  }
`

const consultQueueErrorMessageKeyByTypename = {
  InvalidPatientPhoneNumber: "telephony.consultActions.error.InvalidPatientPhoneNumber",
  NoActiveCallFound: "telephony.consultActions.error.NoActiveCallFound",
  SeveralActiveCallsFound: "telephony.consultActions.error.SeveralActiveCallsFound",
  PatientNotOnCall: "telephony.consultActions.error.PatientNotOnCall",
  ConsultAlreadyInProgress: "telephony.consultActions.error.ConsultAlreadyInProgress",
  ConsultRejected: "telephony.consultActions.error.ConsultRejected",
}

const useConsultQueueMutation = () => {
  const [commit, isInFlight] = useMutation<useConsultQueueMutationType>(consultQueueMutation)
  const commitWithGenesysErrorHandling = useGenesysMutationErrorHandling(
    commit,
    (response) => response.consultQueue.errors,
    consultQueueErrorMessageKeyByTypename,
  )

  const consultQueue = (
    consultQueueInput: ConsultQueueInput,
    onConsultStarted: VoidFunction,
    onAgentNotAuthenticated: VoidFunction,
  ) => {
    commitWithGenesysErrorHandling({
      variables: { input: consultQueueInput },
      onCompleted: onConsultStarted,
      onAgentNotAuthenticated,
    })
  }

  return [consultQueue, isInFlight] as const
}

export { useConsultQueueMutation }
